import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ItemList from "../components/ItemList";
import { EventBusEventsEnum } from "../enums/EventBusEventsEnum";
import EventBus from "../EventBus";
import Razorpay from "../integrations/Razorpay";
import { retrieveFromSessionStorage, saveToSessionStorage } from "../utils/utils";
import TopBar from "../components/common/TopBar";
import CardTitle from "../components/common/CardTitle";
import { RequestedForEnum } from "../enums/RequestedForEnum";
import RazorpayPayment from "../interfaces/razorpayPayment";
import { RazorpayTransactionStatesEnum } from "../enums/RazorpayTransactionStatesEnum";
import { PaymentStatusEnum } from "../enums/PaymentStatusEnum";
import { FirebaseCleverTabRecordEvent } from "../firebase";
import { EventNames } from "../firebase/EventNames";
import { ScreenNames } from "../firebase/ScreenNames";
import { EventProperties } from "../firebase/EventProperties";
import OmsMapping from "../interfaces/OmsMapping";
import OmsService from "../services/omsService";
import OneMealServiceTimeExtensionService from "../services/oneMealServiceTimeExtensionService";

const omsService = new OmsService();
const oneMealServiceTimeExtensionService = new OneMealServiceTimeExtensionService();

export default function OneMealServiceTimeExtensionPayment() {
    const [details, setDetails] = useState<any>();
    const [oneMealService, setOneMealService] = useState<OmsMapping>();
    const [loading, setLoading] = useState<boolean>(true);
    const [useWalletBalance, setUseWalletBalance] = useState<boolean>(true);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    useEffect(() => {
        const token = searchParams.get("token") || retrieveFromSessionStorage("token");
        const extensionId = searchParams.get("extensionId") || retrieveFromSessionStorage("oneMealServiceExtensionId");

        if (token && extensionId) {
            saveToSessionStorage("token", token);
            saveToSessionStorage("oneMealServiceExtensionId", extensionId);
            saveToSessionStorage("requestedFor", RequestedForEnum.ONE_MEAL_SERVICE_TIME_EXTENSION);
            FirebaseCleverTabRecordEvent(EventNames.OKANE_PAGE_VIEWED, {
                [EventProperties.PAGE_NAME]: ScreenNames.ONE_MEAL_SERVICE_TIME_EXTENSION_PAYMENT,
            });
            return;
        }

        navigate("/expired");
    }, [navigate, searchParams]);

    useEffect(() => {
        setLoading(true);
        const token = searchParams.get("token") || retrieveFromSessionStorage("token");
        const extensionId = Number(
            searchParams.get("extensionId") || retrieveFromSessionStorage("oneMealServiceExtensionId")
        );

        if (token && extensionId) {
            (async function () {
                const extensionRequest = await oneMealServiceTimeExtensionService.getTimeExtensionDetails(extensionId);
                if (![PaymentStatusEnum.NOT_PAID, PaymentStatusEnum.PAYMENT_IN_PROGRESS].includes(extensionRequest?.status)) {
                    navigate("/expired", { replace: true });
                    return;
                }
                setDetails(extensionRequest);
                if (extensionRequest?.oneMealServiceId) {
                    const omsData = await omsService.getOmsDetails(extensionRequest.oneMealServiceId);
                    setOneMealService(omsData);
                }
                setLoading(false);
            })();
        } else {
            navigate("/expired");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const onSuccessfulPayment = async (extensionId: number, paymentObj: any) => {
        const razorpayPayment: RazorpayPayment = {
            razorpayOrderId: paymentObj.razorpay_order_id,
            razorpayPaymentId: paymentObj.razorpay_payment_id,
            razorpaySignature: paymentObj.razorpay_signature,
            state: RazorpayTransactionStatesEnum.COMPLETED,
        };
        await oneMealServiceTimeExtensionService.updatePaymentStatus(extensionId, razorpayPayment);
        window.location.replace(`/connecting-securely`);
    };

    const onClickPay = async () => {
        if (!details) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Something Went Wrong",
            });
            return;
        }
        const extensionRequest = await oneMealServiceTimeExtensionService.initiatePayment(details.id, {
            useWalletCash: useWalletBalance,
        });
        if (!extensionRequest) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Something Went Wrong",
            });
            return;
        }
        saveToSessionStorage("transactionId", details.id);
        saveToSessionStorage("requestedFor", RequestedForEnum.ONE_MEAL_SERVICE_TIME_EXTENSION);
        if (extensionRequest?.status === PaymentStatusEnum.PAID) {
            navigate("/success", { replace: true });
            return;
        }
        if (!extensionRequest?.razorpayOrderReferenceId) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Unable to initiate payment",
            });
            return;
        }
        const rzpy = new Razorpay();
        FirebaseCleverTabRecordEvent(EventNames.OKANE_PAY_NOW_CLICKED, {
            [EventProperties.AMOUNT]: extensionRequest.amount,
        });
        await rzpy.initiateRazorPayPaymentFlow(
            {
                id: details.id,
                amount: Number(extensionRequest.amount),
                orderId: extensionRequest.razorpayOrderReferenceId,
                email: oneMealService?.ownerEmail || "",
                fname: oneMealService?.customerName || "",
                mobile: oneMealService?.customerPhoneNumber || "",
            },
            onSuccessfulPayment
        );
    };

    return (
        <div
            className="d-flex-column-center"
            style={{
                width: "100%",
                height: "100%",
                maxWidth: "500px",
                justifyContent: "flex-start",
                gap: 20,
            }}
        >
            <div className="d-flex-column-center" style={{ width: "100%", gap: 20 }}>
                <TopBar title={"Summary"} />
                <div
                    className="d-flex-column-center"
                    style={{
                        width: "100%",
                        height: "100%",
                        gap: 20,
                        justifyContent: "flex-start",
                    }}
                >
                    <CardTitle title={"Extension Details"} />
                    {!loading && (
                        <div
                            className="d-flex-column-center"
                            style={{
                                width: "95%",
                                justifyContent: "flex-start",
                                alignItems: "flex-start",
                                gap: 10,
                            }}
                        >
                            <div className="card">
                                <ItemList
                                    title="Extend By"
                                    action={() => `${details?.extendBy ?? "..."} hour(s)`}
                                />
                                {details?.newSlotSize != null && (
                                    <ItemList
                                        title="Slot Size"
                                        action={() => `${details?.previousSlotSize} hr → ${details?.newSlotSize} hr`}
                                    />
                                )}
                                <div
                                    style={{
                                        borderTop: "dashed",
                                        borderWidth: 0.5,
                                        color: "#BDBDBD",
                                    }}
                                />
                                <ItemList
                                    title="Total Amount"
                                    action={() => `Rs ${details?.grossAmount ?? "..."}`}
                                />
                                {!!details?.walletBalanceUsed && (
                                    <ItemList
                                        title="Wallet Balance Used"
                                        action={() => `Rs ${details?.walletBalanceUsed}`}
                                    />
                                )}
                                <div
                                    style={{
                                        borderTop: "dashed",
                                        borderWidth: 0.5,
                                        color: "#BDBDBD",
                                    }}
                                />
                                <ItemList
                                    title="Customer Name"
                                    action={() => `${oneMealService?.customerName || "..."}`}
                                />
                                <ItemList
                                    title="Customer Phone Number"
                                    action={() => `${oneMealService?.customerPhoneNumber || "..."}`}
                                />
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    marginTop: "10px",
                                }}
                            >
                                <input
                                    type="checkbox"
                                    id="useWalletBalance"
                                    checked={useWalletBalance}
                                    onChange={() => setUseWalletBalance(!useWalletBalance)}
                                    style={{
                                        width: "16px",
                                        height: "16px",
                                        cursor: "pointer",
                                    }}
                                />
                                <label
                                    htmlFor="useWalletBalance"
                                    style={{
                                        cursor: "pointer",
                                        fontSize: "15px",
                                        color: "#333",
                                    }}
                                >
                                    Use Wallet Balance
                                </label>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div style={{ width: "100%" }}>
                <div
                    className="d-flex-column-center"
                    style={{
                        width: "95%",
                        padding: "10px",
                    }}
                >
                    {!loading && (
                        <ItemList
                            title={`Rs ${details?.amount ?? "..."}`}
                            subtitle={"Net payable amount"}
                            action={() => (
                                <button
                                    style={{
                                        color: "white",
                                        backgroundColor: "black",
                                        borderRadius: "9px",
                                        padding: "12px 22px",
                                        cursor: "pointer",
                                        border: "none",
                                    }}
                                    onClick={onClickPay}
                                >
                                    Pay Now
                                </button>
                            )}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
